import { type BlocksContent } from '@strapi/blocks-react-renderer'
import { ROUTE } from 'shared-constants'

export type TradeTier = {
  name: string
  range: string
  threshold: number
  tagline: string
}

export type TradePerk = {
  name: string
  desc: string
  fromTier: number
  badge?: string
}

export type TradePerkCategory = {
  key: 'Spend' | 'Support' | 'Network' | 'Grow'
  tableLabel: string
  titleLead: string
  titleEmphasis: string
  summaryLabel: string
  icon: 'tag' | 'chat' | 'people' | 'mega'
  perks: TradePerk[]
}

export const TRADE_TIERS: TradeTier[] = [
  { name: 'Member', range: '$0–$2.5K', threshold: 0, tagline: '' },
  { name: 'Bronze', range: '$2.5K–$25K', threshold: 2500, tagline: '' },
  {
    name: 'Silver',
    range: '$25K–$50K',
    threshold: 25000,
    tagline: 'Where your perks accelerate',
  },
  { name: 'Gold', range: '$50K–$100K', threshold: 50000, tagline: '' },
  {
    name: 'Platinum',
    range: '$100K+',
    threshold: 100000,
    tagline: 'Benefit like an Expert',
  },
]

export const TRADE_PERK_CATEGORIES: TradePerkCategory[] = [
  {
    key: 'Spend',
    tableLabel: 'Spend',
    titleLead: 'Exclusive',
    titleEmphasis: 'Pricing & Perks',
    summaryLabel: 'Pricing & Perks',
    icon: 'tag',
    perks: [
      {
        name: 'Tax exemption',
        desc: 'Upload your documents to receive tax-exempt status',
        fromTier: 0,
      },
      {
        name: 'Price match promise',
        desc: "Shop with confidence knowing we match the trade price you'd get going direct",
        fromTier: 0,
      },
      {
        name: 'Exclusive trade pricing up to 35% off',
        desc: 'Deep discounts and exclusive trade offers during promotions',
        fromTier: 0,
      },
    ],
  },
  {
    key: 'Support',
    tableLabel: 'Support',
    titleLead: 'Exceptional',
    titleEmphasis: 'Service & Support',
    summaryLabel: 'Service & Support',
    icon: 'chat',
    perks: [
      {
        name: 'Order dashboard',
        desc: "Access all your firm's orders and track shipments in one centralized hub",
        fromTier: 0,
      },
      {
        name: 'Dedicated customer service team',
        desc: 'Email our team of Trade Concierge Specialists for fast answers and quotes',
        fromTier: 1,
      },
      {
        name: 'Proactive order monitoring',
        desc: "Our team ensures orders are on time and that you're up to date every step of the way",
        fromTier: 2,
      },
      {
        name: 'Dedicated sourcing specialist & account manager',
        desc: 'A member of your design team who partners with you to maximize your benefits and provides hands-on sourcing and customization support',
        fromTier: 2,
      },
    ],
  },
  {
    key: 'Network',
    tableLabel: 'Network',
    titleLead: 'Community',
    titleEmphasis: '& Connection',
    summaryLabel: 'Community',
    icon: 'people',
    perks: [
      {
        name: 'Invitations to digital events',
        desc: 'Get a front row seat to intimate conversations with our Experts and other industry thought leaders',
        fromTier: 0,
      },
      {
        name: 'Access to business workshops',
        desc: 'Tune into Expert-led workshops on pricing, marketing, and the business of running a design practice',
        fromTier: 1,
      },
      {
        name: 'In-person event invites',
        desc: 'Invitations to intimate dinners, cocktail parties, and gatherings with our design community',
        fromTier: 2,
      },
    ],
  },
  {
    key: 'Grow',
    tableLabel: 'Grow',
    titleLead: 'Publicity',
    titleEmphasis: '& Growth',
    summaryLabel: 'Publicity & Growth',
    icon: 'mega',
    perks: [
      {
        name: 'Social media features',
        desc: 'Showcase your work on our social networks to half a million followers and potential clients',
        fromTier: 1,
      },
      {
        name: 'Email features',
        desc: 'Have your work featured in a dedicated trade email to hundreds of thousands of readers (1 per calendar year)',
        fromTier: 2,
      },
      {
        name: 'Editorial coverage',
        desc: "Be featured in The Expert's editorial, from home tours to trend stories (1 per calendar year)",
        fromTier: 3,
      },
      {
        name: 'Expert profile page',
        desc: 'A dedicated profile where prospective clients can discover your work and book a consultation with you',
        fromTier: 3,
      },
      {
        name: 'Showroom curation',
        desc: 'Curate a shoppable Showroom on The Expert filled with the brands and pieces you love',
        fromTier: 3,
      },
      {
        name: 'Top Expert status',
        desc: 'Be recognized among our Top Experts, with priority consideration for features, partnerships, and new opportunities',
        fromTier: 4,
        badge: 'Priority consideration',
      },
    ],
  },
]

export const ALL_TRADE_PERKS: TradePerk[] = TRADE_PERK_CATEGORIES.flatMap(
  (category) => category.perks
)

export const DEFAULT_EXPLORER_TIER_INDEX = 1

export const getUnlockedPerkCount = (tierIndex: number): number =>
  ALL_TRADE_PERKS.filter((perk) => tierIndex >= perk.fromTier).length

export const getNewPerksAtTier = (tierIndex: number): TradePerk[] =>
  ALL_TRADE_PERKS.filter((perk) => perk.fromTier === tierIndex)

export const getCategoryFraction = (
  categoryKey: TradePerkCategory['key'],
  tierIndex: number
): { unlocked: number; total: number } => {
  const category = TRADE_PERK_CATEGORIES.find((c) => c.key === categoryKey)
  const perks = category?.perks ?? []
  return {
    unlocked: perks.filter((perk) => tierIndex >= perk.fromTier).length,
    total: perks.length,
  }
}

export const TRADE_VALUE_PROPS: {
  eyebrow: string
  title: string
  desc: string
  icon: 'search' | 'shield' | 'growth' | 'users'
}[] = [
  {
    eyebrow: 'Discovery & Sourcing',
    title: 'One Place to Source It All',
    desc: 'Hundreds of brands. Every category. One platform built around how designers actually work.',
    icon: 'search',
  },
  {
    eyebrow: 'Reliable',
    title: 'Support That Earns Your Trust',
    desc: 'Proactive updates, straight answers, and trade pricing that holds.',
    icon: 'shield',
  },
  {
    eyebrow: 'Partnership',
    title: 'Benefits That Grow With You',
    desc: 'Trade Perks unlock elevated service and exclusive access that grow with your business.',
    icon: 'growth',
  },
  {
    eyebrow: 'Community',
    title: 'A Community Worth Joining',
    desc: 'Trade events, educational programming, and a network of designers at your level.',
    icon: 'users',
  },
]

export const TRADE_CHANNELS: { name: string; href: string }[] = [
  { name: 'The Expert', href: 'https://www.theexpert.com/' },
  { name: 'Interior Define', href: 'https://www.interiordefine.com/' },
  { name: 'The Citizenry', href: 'https://www.the-citizenry.com/' },
  { name: 'St. Frank', href: 'https://www.stfrank.com/' },
  { name: 'The Inside', href: 'https://www.theinside.com/' },
]

export const TRADE_OUR_BRANDS: {
  name: string
  desc: string
  href: string
  logoSrc: string
  // Optical correction: logos with lower aspect ratios (or lighter strokes)
  // read smaller at the same cap height, so they get a boost > 1
  logoScale: number
}[] = [
  {
    name: 'INTERIOR DEFINE',
    desc: 'Custom upholstery. 200+ fabrics, performance textiles, configurable dimensions.',
    href: 'https://www.interiordefine.com/',
    logoSrc: '/tradeBrands/interior_define_logo.svg',
    logoScale: 1,
  },
  {
    name: 'THE CITIZENRY',
    desc: 'Globally-sourced furniture, rugs, and bedding. 100% fair trade, guaranteed by WFTO.',
    href: 'https://www.the-citizenry.com/',
    logoSrc: '/tradeBrands/the_citizenry_logo.svg',
    logoScale: 1,
  },
  {
    name: 'ST. FRANK',
    desc: 'Artisan-inspired textiles: fabric, wallpaper, pillows, and window treatments.',
    href: 'https://www.stfrank.com/',
    logoSrc: '/tradeBrands/st_frank_logo.svg',
    logoScale: 1.1,
  },
  {
    name: 'THE INSIDE',
    desc: 'Bold, print-driven, made-to-order furniture.',
    href: 'https://www.theinside.com/',
    logoSrc: '/tradeBrands/the_inside_logo.svg',
    logoScale: 1.45,
  },
]

export const PERKS_DISCLAIMER =
  '* Perks accumulate on theexpert.com, interiordefine.com, the-citizenry.com, theinside.com, stfrank.com, or any Interior Define or The Citizenry retail location.'

export const TRADE_FORM_DESCRIPTION_BLOCKS: BlocksContent = [
  {
    type: 'heading',
    level: 2,
    children: [{ type: 'text', text: 'Apply now' }],
  },
]

export const TRADE_FORM_CONSENT_BLOCKS: BlocksContent = [
  {
    type: 'paragraph',
    children: [
      {
        type: 'text',
        text: 'By applying to The Expert Trade Program, you are accepting The Expert ',
      },
      {
        type: 'link',
        url: ROUTE.PRIVACY_POLICY(),
        children: [{ type: 'text', text: 'privacy notice' }],
      },
      { type: 'text', text: ', ' },
      {
        type: 'link',
        url: ROUTE.TERMS_OF_USE(),
        children: [{ type: 'text', text: 'Terms of Service' }],
      },
      { type: 'text', text: ' and the ' },
      {
        type: 'link',
        url: '/trade-program-agreement',
        children: [
          { type: 'text', text: 'The Expert Trade Program Agreement' },
        ],
      },
      { type: 'text', text: '.' },
    ],
  },
]
